import './ConsentsStep.scss';

import { marked } from 'marked';
import React, { useState } from 'react';

import { CheckboxField } from '../components/common/CheckboxField';
import IconChevronDown from '../components/common/IconChevronDown';

type Consent = { consent_uri: string; consent_text: string };

type Props = {
  dnaVisitConsent: Consent | null;
  hieConsent: Consent | null;
  researchConsent: Consent | null;
  transactionalSmsConsent: Consent | null;
  marketingConsent: Consent | null;
  minorConsent: Consent | null;
  allConsents: Consent[];
  values: {
    consents?: {
      acceptGeneral?: boolean;
      acceptDNAVisit?: boolean;
      familyHistoryAttest?: boolean;
      acceptHIE?: boolean;
      acceptResearchContact?: boolean;
      acceptTransactionalSms?: boolean;
      acceptMarketing?: boolean;
      acceptMinor?: boolean;
    };
  };
  showErrors: boolean;
  errors: Record<string, string>;
  update: (path: string, val: unknown) => void;
  includeFamilyHistoryAttest?: boolean;
};

type SectionKey = 'minor' | 'general' | 'dnavisit' | 'hie' | 'research' | 'transactionalSms' | 'marketing';

// Preselected consents (acceptMinor / acceptHIE / acceptTransactionalSms) start
// collapsed. Required unchecked sections start expanded.
const DEFAULT_OPEN: Record<SectionKey, boolean> = {
  minor: false,
  general: true,
  dnavisit: true,
  hie: false,
  research: false,
  transactionalSms: false,
  marketing: false,
};

function CollapsibleConsentSection({
  sectionKey,
  title,
  bodyHtml,
  isOpen,
  onToggle,
  children,
}: {
  sectionKey: SectionKey;
  title: string;
  bodyHtml: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const headerId = `consent-${sectionKey}-header`;
  const bodyId = `consent-${sectionKey}-body`;
  return (
    <div className='d-flex flex-column gap-2'>
      <div className='field outlined collapsible-consent'>
        <button
          type='button'
          className={`collapsible-consent__header ${isOpen ? 'is-open' : ''}`}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          id={headerId}
          onClick={onToggle}
        >
          <span className='collapsible-consent__title'>{title}</span>
          <IconChevronDown className='collapsible-consent__chevron' />
        </button>
        {isOpen && (
          <div
            id={bodyId}
            role='region'
            aria-labelledby={headerId}
            className='consents-step__scroll collapsible-consent__body'
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        )}
      </div>
      {children}
    </div>
  );
}

export default function ConsentsStep({
  dnaVisitConsent,
  hieConsent,
  researchConsent,
  transactionalSmsConsent,
  marketingConsent,
  minorConsent,
  allConsents,
  values,
  showErrors,
  errors,
  update,
  includeFamilyHistoryAttest = true,
}: Props) {
  const [open, setOpen] = useState<Record<SectionKey, boolean>>(DEFAULT_OPEN);

  const toggleSection = (k: SectionKey) => setOpen(prev => ({ ...prev, [k]: !prev[k] }));
  const collapseSection = (k: SectionKey) => setOpen(prev => ({ ...prev, [k]: false }));

  const handleAccept = (k: SectionKey, path: string, val: boolean) => {
    update(path, val);
    // Auto-collapse the section once the user accepts it; leave it as-is
    // (user-controlled) when they uncheck so they can re-read freely.
    if (val) collapseSection(k);
  };

  const generalBodyHtml = allConsents.map(c => marked.parse(c.consent_text || '')).join('');

  return (
    <div className='field-span-2 d-flex flex-column gap-3'>
      {/* Minor (parent/guardian) consent: only present for minor orders; sits at the top, pre-checked and required. */}
      {minorConsent && (
        <CollapsibleConsentSection
          sectionKey='minor'
          title='Parent or Guardian Consent for a Minor'
          bodyHtml={marked.parse(minorConsent.consent_text || '') as string}
          isOpen={open.minor}
          onToggle={() => toggleSection('minor')}
        >
          <CheckboxField
            required
            checked={!!values.consents?.acceptMinor}
            onChange={val => handleAccept('minor', 'consents.acceptMinor', val)}
            label='I accept the Parent or Guardian Consent for a Minor'
            showError={showErrors && !!errors['consents.minor']}
            error={'Required'}
          />
        </CollapsibleConsentSection>
      )}

      {/* General informed consent + product consents.
          Family-history attestation lives at the top of this group per product. */}
      {includeFamilyHistoryAttest && (
        <CheckboxField
          required
          checked={!!values.consents?.familyHistoryAttest}
          onChange={val => update('consents.familyHistoryAttest', val)}
          label={
            'I attest that I am not using this test for a known personal or family history of inherited conditions'
          }
          showError={showErrors && !!errors['consents.family']}
          error={'Required'}
        />
      )}

      <CollapsibleConsentSection
        sectionKey='general'
        title='Consents & agreements'
        bodyHtml={generalBodyHtml}
        isOpen={open.general}
        onToggle={() => toggleSection('general')}
      >
        <CheckboxField
          required
          checked={!!values.consents?.acceptGeneral}
          onChange={val => handleAccept('general', 'consents.acceptGeneral', val)}
          label="I accept MyOme's informed consent"
          showError={showErrors && !!errors['consents.general']}
          error={'Required'}
        />
      </CollapsibleConsentSection>

      {/* DNAvisit consent */}
      {dnaVisitConsent && (
        <CollapsibleConsentSection
          sectionKey='dnavisit'
          title='DNAvisit physician-of-record informed consent'
          bodyHtml={marked.parse(dnaVisitConsent.consent_text || '') as string}
          isOpen={open.dnavisit}
          onToggle={() => toggleSection('dnavisit')}
        >
          <CheckboxField
            required
            checked={!!values.consents?.acceptDNAVisit}
            onChange={val => handleAccept('dnavisit', 'consents.acceptDNAVisit', val)}
            label={'I accept the DNAvisit physician-of-record informed consent'}
            showError={showErrors && !!errors['consents.dnavisit']}
            error={'Required'}
          />
        </CollapsibleConsentSection>
      )}

      {/* HIE consent (preselected → starts collapsed) */}
      {hieConsent && (
        <CollapsibleConsentSection
          sectionKey='hie'
          title='Health Information Exchange (HIE)'
          bodyHtml={marked.parse(hieConsent.consent_text || '') as string}
          isOpen={open.hie}
          onToggle={() => toggleSection('hie')}
        >
          <CheckboxField
            checked={!!values.consents?.acceptHIE}
            onChange={val => handleAccept('hie', 'consents.acceptHIE', val)}
            label={
              'I agree to participate in Health Information Exchange (HIE). Opting out may reduce the accuracy of our analyses for you.'
            }
          />
        </CollapsibleConsentSection>
      )}

      {/* Research contact consent (optional → starts collapsed) */}
      {researchConsent && (
        <CollapsibleConsentSection
          sectionKey='research'
          title='Research & clinical trial contact'
          bodyHtml={marked.parse(researchConsent.consent_text || '') as string}
          isOpen={open.research}
          onToggle={() => toggleSection('research')}
        >
          <CheckboxField
            checked={!!values.consents?.acceptResearchContact}
            onChange={val => handleAccept('research', 'consents.acceptResearchContact', val)}
            label={
              'MyOme may contact me about research studies or clinical trials that may be relevant to my genetic test results or health information related to MyOme testing services.'
            }
          />
        </CollapsibleConsentSection>
      )}

      {/* Transactional SMS consent (preselected → starts collapsed) */}
      {transactionalSmsConsent && (
        <CollapsibleConsentSection
          sectionKey='transactionalSms'
          title='SMS and Phone Communication for Order Updates'
          bodyHtml={marked.parse(transactionalSmsConsent.consent_text || '') as string}
          isOpen={open.transactionalSms}
          onToggle={() => toggleSection('transactionalSms')}
        >
          <CheckboxField
            checked={!!values.consents?.acceptTransactionalSms}
            onChange={val => handleAccept('transactionalSms', 'consents.acceptTransactionalSms', val)}
            label={
              'MyOme may contact me via phone call or text message regarding updates related to this order and testing services.'
            }
          />
        </CollapsibleConsentSection>
      )}

      {/* Marketing consent (optional → starts collapsed) */}
      {marketingConsent && (
        <CollapsibleConsentSection
          sectionKey='marketing'
          title='Marketing Contact'
          bodyHtml={marked.parse(marketingConsent.consent_text || '') as string}
          isOpen={open.marketing}
          onToggle={() => toggleSection('marketing')}
        >
          <CheckboxField
            checked={!!values.consents?.acceptMarketing}
            onChange={val => handleAccept('marketing', 'consents.acceptMarketing', val)}
            label={
              'I agree to receive marketing or promotional communications from MyOme via email, phone call, or text message.'
            }
          />
        </CollapsibleConsentSection>
      )}
    </div>
  );
}
